import React from 'react';

import AppStoreBadges from '@components/AppStoreBadges';
import ClientRenderedContent from '@components/ClientRenderedContent';
import CookiesSettingsBtn from '@components/CookiesSettingsBtn';
import useCurrentUser from '@hooks/useCurrentUser';
import useEventTracking from '@hooks/useEventTracking';
import useNewsletters from '@hooks/useNewsletters';
import useServerSideRendering from '@hooks/useServerSideRendering';
import useWindowSize from '@hooks/useWindowSize';
import { Theme } from '@interfaces/global';
import { newsletter_list_path, referrals_path, upgrade_subscription_path } from '@routes';
import { ClickTarget, TrackingLocation, click } from '@tracking';
import { Newsletter } from '@utils/backend-api/newsletters';
import { LeadDetails } from '@utils/backend-api/users';
import { WIDTH_SM } from '@utils/constants';
import { FormLocation, LeadTypes } from '@utils/enums';
import { transformToSnakeCase } from '@utils/formatting';

import Button from '../Button';
import { PrimaryButton, ProButton } from '../Button/variants';
import EmailLeadCapture from '../EmailLeadCapture';
import { MORE_SUBMENU_SOCIAL_NAV_ITEMS } from '../Nav/navItems';
import { MORE_LINK_ITEMS } from './footerItems';

const TARGET_NEWSLETTERS_NAME = [
  'AI Agenda',
  'AI Infrastructure',
  'Most Popular Stories',
  'The Briefing',
  'The Information Finance',
  'The Weekend',
];

interface FormAndSocialProps {
  currentUserLoggedin: boolean;
  isContentPage: boolean;
  isPro?: boolean;
  isSubscriber: boolean;
  leadDetails: LeadDetails;
  subscribePathWithPossibleOffer: string;
}

const FormAndSocial = ({
  currentUserLoggedin,
  isContentPage,
  isPro,
  isSubscriber,
  leadDetails,
  subscribePathWithPossibleOffer,
}: FormAndSocialProps) => {
  const hydrated = useServerSideRendering();
  const { width: windowWidth } = useWindowSize();
  const { trackEvent } = useEventTracking();
  const { newsletters } = useNewsletters();

  const getFormHeader = () => {
    if (!hydrated) {
      return null;
    }
    if (!isSubscriber && !currentUserLoggedin && isContentPage) {
      return 'Sign up for newsletters';
    } else if (!isSubscriber) {
      return 'Subscribe to The Information';
    } else if (isSubscriber && !isPro) {
      return 'Become a Pro subscriber';
    } else {
      return 'Share The Information';
    }
  };

  const getTargetNewsletters = () => {
    return newsletters
      ?.filter((newsletter: Newsletter) => TARGET_NEWSLETTERS_NAME.includes(newsletter.name))
      ?.map((newsletter: Newsletter) => newsletter.id);
  };

  const getFormAction = () => {
    if (!isSubscriber && !currentUserLoggedin) {
      return (
        <EmailLeadCapture
          buttonText={isContentPage ? 'Sign up' : 'Get started'}
          formLocation={FormLocation.Footer}
          formClassName={isContentPage ? 'min-w-[350px]' : undefined}
          redirectToOnSuccess={
            isContentPage ? newsletter_list_path() : subscribePathWithPossibleOffer
          }
          leadType={isContentPage ? LeadTypes.FooterTargetNewsletters : LeadTypes.Footer}
          theme={Theme.Dark}
          isVertical={windowWidth < WIDTH_SM}
          buttonClassName="w-full sm:w-[120px]"
          leadDetails={leadDetails}
          isPro={isPro}
          newsletterIds={isContentPage ? getTargetNewsletters() : []}
          inputClassName="w-full placeholder:text-grayscale-beige placeholder:opacity-50 dark:placeholder:opacity-100 dark:bg-[var(--light-grayscale-charcoal)]"
          includeNewsletterOption={isContentPage}
          includeSignInOption={!isContentPage}
          gdprClassName="mb-8 sm:mb-16"
        />
      );
    } else if (!isSubscriber && currentUserLoggedin) {
      return (
        <PrimaryButton
          as="link"
          href={subscribePathWithPossibleOffer}
          onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.GetStarted }))}
        >
          Get started
        </PrimaryButton>
      );
    } else if (!isPro && isSubscriber) {
      return (
        <ProButton
          as="link"
          href={upgrade_subscription_path()}
          onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.Upgrade }))}
        >
          Upgrade
        </ProButton>
      );
    } else {
      return (
        <PrimaryButton
          as="link"
          href={referrals_path()}
          onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.ReferAFriend }))}
        >
          Refer a friend
        </PrimaryButton>
      );
    }
  };
  const { currentUser } = useCurrentUser();
  const ARBITRATY_CONSTANT = 600_000;
  const userIdParam = currentUser?.id
    ? new Date(currentUser.id * ARBITRATY_CONSTANT).toUTCString()
    : '';

  return (
    <div className="flex w-full flex-col pr-0 pt-72 sm:w-auto sm:pt-0 md:pr-112">
      <div>
        <div className="mb-24 font-suisse-works text-subheadline-sm leading-[164%] text-white sm:text-headline-xs">
          {getFormHeader()}
        </div>
        {hydrated && getFormAction()}
      </div>
      <div className="mt-48">
        <div className="mb-32 flex w-full justify-around sm:w-[180px] sm:justify-between">
          {MORE_SUBMENU_SOCIAL_NAV_ITEMS.map(({ icon: Icon, name, ...rest }) => (
            <Button
              className="flex h-[21px] w-[21px] items-center justify-center bg-transparent text-white transition-colors hover:border-primary-magenta hover:text-primary-magenta focus:border-primary-magenta focus:text-primary-magenta dark:hover:text-primary-pink"
              as="externalLink"
              key={name}
              onClickCapture={() => trackEvent(click({ clickTarget: transformToSnakeCase(name) }))}
              {...rest}
            >
              <Icon className="h-[21px]" />
              <span className="sr-only">{name}</span>
            </Button>
          ))}
        </div>
        <div className="mb-32 flex flex-wrap gap-12">
          <AppStoreBadges trackingLocation={TrackingLocation.Footer} />
        </div>
        <div className="mb-8 flex gap-20">
          {MORE_LINK_ITEMS.map(({ name, href, ...rest }, index) => (
            <Button
              className="mt-8 !w-auto !justify-start font-suisse-screen text-overline-md text-grayscale-beige no-underline opacity-50 transition-all hover:text-primary-magenta hover:opacity-100 focus:text-primary-magenta dark:text-white dark:hover:text-primary-pink"
              as="link"
              key={href}
              href={href}
              onClickCapture={() => trackEvent(click({ clickTarget: transformToSnakeCase(name) }))}
              title={index === 2 ? `${name} ${userIdParam}` : name}
              {...rest}
            >
              {name}
            </Button>
          ))}
        </div>
        <div className="mt-8 font-suisse-screen text-overline-md text-grayscale-beige opacity-50 dark:text-white">
          &#169; 2013&ndash;
          <ClientRenderedContent>{new Date().getFullYear()}</ClientRenderedContent> The Information.
          All Rights Reserved.
        </div>

        <CookiesSettingsBtn />
      </div>
    </div>
  );
};

export default FormAndSocial;
